import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CertificateContext = createContext();

export const useCertificate = () => {
  const context = useContext(CertificateContext);
  if (!context) {
    throw new Error('useCertificate must be used within a CertificateProvider');
  }
  return context;
};

export const CertificateProvider = ({ children }) => {
  const { token } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Chargement des certificats ───────────────────────────────────────────
  const loadMyCertificates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/certificates');
      setCertificates(data.certificates || []);
      return { success: true, certificates: data.certificates || [] };
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger automatiquement à la connexion
  useEffect(() => {
    if (token) loadMyCertificates();
    else setCertificates([]);
  }, [token, loadMyCertificates]);

  // ── Récupérer un certificat par ID ───────────────────────────────────────
  const getCertificateById = useCallback(async (certificateId) => {
    // Cache d'abord
    const cached = certificates.find(c => c.id === certificateId);
    if (cached) return { success: true, certificate: cached };
    try {
      const { data } = await axios.get(`/api/certificates/${certificateId}`);
      return { success: true, certificate: data.certificate };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, [certificates]);

  // ── Vérification publique d'un certificat ────────────────────────────────
  const verifyCertificate = useCallback(async (verificationCode) => {
    try {
      const { data } = await axios.get(`/api/certificates/verify/${verificationCode}`);
      return {
        success: data.success !== false,
        valid: data.valid,
        certificate: data.certificate,
        error: data.error,
      };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, []);

  // ── Forcer la tentative d'émission (si éligible) ─────────────────────────
  const issueCertificate = useCallback(async (courseId) => {
    try {
      const { data } = await axios.post(`/api/courses/${courseId}/issue-certificate`);
      if (data.certificate) {
        setCertificates(prev => {
          const exists = prev.some(c => c.id === data.certificate.id);
          return exists ? prev : [data.certificate, ...prev];
        });
      }
      return data;
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, []);

  // ── Accesseurs synchrones ─────────────────────────────────────────────────
  const getUserCertificates = useCallback(() => certificates, [certificates]);

  const getCertificateStats = useCallback(() => {
    const avg = certificates.length > 0
      ? certificates.reduce((s, c) => s + (Number(c.score) || 0), 0) / certificates.length
      : 0;
    return {
      totalCertificates: certificates.length,
      issuedCertificates: certificates.filter(c => c.status === 'issued').length,
      professionalCertificates: 0,
      averageScore: Math.round(avg),
      recentCertificates: [...certificates]
        .sort((a, b) => new Date(b.issued_at) - new Date(a.issued_at))
        .slice(0, 3),
    };
  }, [certificates]);

  // ── Téléchargement (PDF côté client via print dialog) ────────────────────
  const downloadCertificate = useCallback(async (certificateId) => {
    try {
      // Ouvre la page du certificat dans un nouvel onglet pour impression/PDF
      window.open(`/certificates/${certificateId}?print=1`, '_blank');
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Partage (URL publique de vérification) ───────────────────────────────
  const shareCertificate = useCallback(async (certificateId, platform = 'linkedin') => {
    const cert = certificates.find(c => c.id === certificateId);
    if (!cert) return { success: false, error: 'Certificat non trouvé' };
    const verifyUrl = `${window.location.origin}/verify?code=${encodeURIComponent(cert.verification_code)}`;
    const text = `J'ai obtenu mon certificat pour "${cert.course?.title || 'un cours'}" sur Innovactive360°`;
    let shareLink = verifyUrl;
    switch (platform) {
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(verifyUrl)}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(verifyUrl)}`;
        break;
      default:
        shareLink = verifyUrl;
    }
    return { success: true, shareLink, shareText: text };
  }, [certificates]);

  const value = {
    certificates,
    loading,
    error,
    loadMyCertificates,
    getCertificateById,
    verifyCertificate,
    issueCertificate,
    getUserCertificates,
    getCertificateStats,
    downloadCertificate,
    shareCertificate,
    // Compat ascendante
    templates: [],
    certificateTypes: {
      COURSE_COMPLETION: 'course_completion',
      PROFESSIONAL: 'professional',
    },
    certificateStatus: {
      ISSUED: 'issued',
      REVOKED: 'revoked',
      EXPIRED: 'expired',
    },
  };

  return (
    <CertificateContext.Provider value={value}>
      {children}
    </CertificateContext.Provider>
  );
};
